import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { getChargeById, getPaymentsByChargeId } from '../../services/payments/paymentsService';
import { ChargeDto, PaymentDto, PaymentStatus } from '../../services/payments/types';

const ChargeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  
  const [charge, setCharge] = useState<ChargeDto | null>(null);
  const [payments, setPayments] = useState<PaymentDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChargeData = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to view charge details.');
      return;
    }
    
    if (!id) {
      setError('Charge ID is missing.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [chargeData, paymentsData] = await Promise.all([
        getChargeById(auth.user || null, id),
        getPaymentsByChargeId(auth.user || null, id)
      ]);
      
      setCharge(chargeData);
      setPayments(paymentsData);
    } catch (err) {
      console.error('Error fetching charge details:', err);
      setError('Failed to fetch charge details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated, auth.user, id]);
  
  useEffect(() => {
    fetchChargeData();
  }, [fetchChargeData]);
  
  const handleBack = () => {
    navigate(-1);
  };
  
  // Formatar valores monetários
  const formatCurrency = (amount?: number, currency: string = 'BRL') => {
    if (amount === undefined) return 'N/A';
    
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL'
    }).format(amount / 100); // Assumindo que o valor está em centavos
  };
  
  // Formatar data para o formato local
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Formatar data e hora para o formato local
  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  // Obter classes de CSS para o status do pagamento
  const getPaymentStatusBadgeClasses = (status?: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.APPROVED:
      case PaymentStatus.CAPTURED:
        return 'bg-green-100 text-green-800';
      case PaymentStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case PaymentStatus.CANCELLED:
      case PaymentStatus.EXPIRED:
      case PaymentStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case PaymentStatus.REFUNDED:
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : error ? 'Error' : !charge ? 'Not Found' : 'Charge Details'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Charges", to: "/payments/charges" },
          { label: isLoading ? 'Loading...' : error ? 'Error' : !charge ? 'Not Found' : charge.id || 'Details' }
        ]}
      />
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={fetchChargeData}
            >
              Retry
            </Button>
          </div>
        ) : !charge ? (
          <div className="bg-yellow-50 text-yellow-700 p-4 rounded-md mb-4">
            Charge not found
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={handleBack}
            >
              Back to Charges
            </Button>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Charge Information</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Description</span>
                    <span className="mt-1 block">{charge.description || '-'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Amount</span>
                    <span className="mt-1 block font-medium">
                      {formatCurrency(charge.amount, charge.currency)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Status</span>
                    <span className="mt-1 block">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${
                        charge.charge_status === 'COMPLETED' ? 'green' : 
                        charge.charge_status === 'PENDING' ? 'yellow' : 'red'
                      }-100 text-${
                        charge.charge_status === 'COMPLETED' ? 'green' : 
                        charge.charge_status === 'PENDING' ? 'yellow' : 'red'
                      }-800`}>
                        {charge.charge_status || 'Unknown'}
                      </span>
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Quantity</span>
                    <span className="mt-1 block">{charge.quantity || '1'}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">User & Dates</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">User ID</span>
                    <Link to={`/users/${charge.user_id}`} className="mt-1 block text-blue-600 hover:underline">
                      {charge.user_id}
                    </Link>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Due Date</span>
                    <span className="mt-1 block">{formatDate(charge.due_date)}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Paid Date</span>
                    <span className="mt-1 block">{charge.paid_at ? formatDate(charge.paid_at) : 'Not paid yet'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Created At</span>
                    <span className="mt-1 block">{formatDateTime(charge.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500 block">Updated At</span>
                    <span className="mt-1 block">{formatDateTime(charge.updated_at)}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
                <div className="space-y-3">
                  <Link to={`/payments/charges/${id}/payments`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-credit-card mr-2"></i>
                      View All Payments
                    </Button>
                  </Link>
                  <Link to={`/users/${charge.user_id}/charges`} className="block w-full">
                    <Button variant="secondary" fullWidth>
                      <i className="fas fa-user mr-2"></i>
                      View User Charges
                    </Button>
                  </Link>
                  <Button variant="ghost" fullWidth onClick={handleBack}>
                    <i className="fas fa-arrow-left mr-2"></i>
                    Back
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payments</h3>
              {payments.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500">No payments found for this charge.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Gateway
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Method
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Paid At
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {payment.id?.substring(0, 8)}...
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {payment.payment_gateway || '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {payment.payment_method || '-'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusBadgeClasses(payment.payment_status)}`}>
                              {payment.payment_status || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                            {payment.paid_at ? formatDateTime(payment.paid_at) : 'Not paid yet'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex space-x-1">
                              <Link to={`/payments/charges/${id}/payments/${payment.id}`}>
                                <Button variant="ghost" size="xs" title="View payment details">
                                  <i className="fas fa-eye"></i>
                                </Button>
                              </Link>
                              {payment.receipt_url && (
                                <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer">
                                  <Button variant="ghost" size="xs" title="View receipt">
                                    <i className="fas fa-receipt"></i>
                                  </Button>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ChargeDetails;